import type { CanvasEx } from '@/typings/canvas';
import type { ElementMapOption } from '@/components/DesignStudio/types';
import { colorsMapStore, useColorOptions } from '@/composables/colorOptions';
import type { ColorOptionsApi } from '@/composables/colorOptions';

/**
 * What a canvas element has to expose for its shadow color options to be managed here. Texts and
 *  image placeholders implement the same shape on purpose, so every panel shares this logic.
 */
export interface ShadowColorElement {
    shadowColor?: ElementMapOption;
    shadowColorsMap?: Map<number, ElementMapOption>;
    shadowColorOption?: number;
    shadowColorLibraryId?: number;
    setShadowColor(color: string): void;
    setEditedProp(...props: string[]): void;
}

export interface ShadowColorOptions<T extends ShadowColorElement> {
    /** Getter, not the value: the selected element changes while the panel stays mounted. */
    element: () => T | undefined;
    canvas: () => CanvasEx;
    editMode: () => boolean | undefined;
    /** Opens the library picker, which lives in the AngularJS controller of each step. */
    openLibrarySelector: () => void;
}

export type ShadowColorOptionsApi = ColorOptionsApi;

/**
 * Color options for a drop shadow: the merchant defines one or more colors (or points at a color
 *  library) and the buyer picks one in the storefront.
 *
 * Shared by every shadow panel through useShadowSettings, which only differ in which element they
 *  are pointed at.
 */
export function useShadowColorOptions<T extends ShadowColorElement>(
    options: ShadowColorOptions<T>
): ShadowColorOptionsApi {
    return useColorOptions<T>({
        ...options,
        store: colorsMapStore<T>(
            {
                colorsMap: 'shadowColorsMap',
                option: 'shadowColorOption',
                libraryId: 'shadowColorLibraryId',
                defaultColor: 'shadowColor'
            },
            (element, css) => element.setShadowColor(css)
        )
    });
}
